import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {Router} from "@angular/router";
import {MatIcon} from "@angular/material/icon";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {SupportRequestService} from "../../services/support-request.service";
import {FailedRefundService} from "../../services/failed-refund.service";
import {ActiveUserService} from "../../services/active-user.service";
import {PermissionsService} from "../../services/permissions.service";
import {PermissionTypes} from "../../enum/PermissionTypes";

interface IDashboardTile {
  label: string;
  icon: string;
  /** Absolute route the tile deep-links to. */
  route: string;
  /** Count to show; null while loading. */
  count: number | null;
  /** Whether the count is a "needs attention" (non-zero) value. */
  attention: boolean;
  testId: string;
}

/**
 * Operations dashboard tiles for the landing page (Release 1.19 IA pass, Stage 6).
 *
 * Turns the dead game-picker landing into an at-a-glance ops view. Each tile is a
 * "needs attention" queue, permission-gated, deep-linking to its screen.
 *
 * Currently wired to the counts already exposed globally (support tickets, failed
 * refunds). Per-game queues (winner/charity payouts, draws due) need cross-game
 * aggregate endpoints before they can be shown globally — the tile list is built
 * declaratively so they drop in once those exist.
 */
@Component({
  selector: 'app-dashboard-tiles',
  standalone: true,
  imports: [MatIcon, MatProgressSpinner],
  templateUrl: './dashboard-tiles.component.html',
  styleUrl: './dashboard-tiles.component.scss',
})
export class DashboardTilesComponent implements OnInit {
  private router = inject(Router);
  private supportRequestService = inject(SupportRequestService);
  private failedRefundService = inject(FailedRefundService);
  private activeUserService = inject(ActiveUserService);
  private permissions = inject(PermissionsService);

  private readonly canViewSupport = this.permissions.userHasPermission(PermissionTypes.ViewSupportRequests);
  private readonly canViewFailedRefunds = this.permissions.userHasPermission(PermissionTypes.ViewFailedRefunds);

  private supportCount = signal<number | null>(null);
  private failedRefundsCount = signal<number | null>(null);

  public tiles = computed<IDashboardTile[]>(() => {
    const tiles: IDashboardTile[] = [];
    if (this.canViewSupport) {
      tiles.push({
        label: 'Open support tickets',
        icon: 'support_agent',
        route: '/cross-game-support-desk-search',
        count: this.supportCount(),
        attention: (this.supportCount() ?? 0) > 0,
        testId: 'dashboard-tile-support',
      });
    }
    if (this.canViewFailedRefunds) {
      tiles.push({
        label: 'Failed refunds',
        icon: 'money_off',
        route: '/failed-refunds',
        count: this.failedRefundsCount(),
        attention: (this.failedRefundsCount() ?? 0) > 0,
        testId: 'dashboard-tile-failed-refunds',
      });
    }
    return tiles;
  });

  ngOnInit(): void {
    if (this.canViewSupport) {
      const userId = this.activeUserService.activeUser()?.Id;
      if (userId) {
        this.supportRequestService.fetchMyAssignedRequests(userId, null).subscribe({
          next: requests => this.supportCount.set(requests.filter(r => !r.Resolved).length),
          error: () => this.supportCount.set(null),
        });
      }
    }
    if (this.canViewFailedRefunds) {
      this.failedRefundService.getAllFailedRefunds().subscribe({
        next: refunds => this.failedRefundsCount.set(refunds.length),
        error: () => this.failedRefundsCount.set(null),
      });
    }
  }

  public openTile(tile: IDashboardTile): void {
    void this.router.navigateByUrl(tile.route);
  }
}
